<!--
  Lookup feedback lives in a template so it can be placed wherever the postcode
  actually is. Consumers split the component across two instances (postcode here,
  address there, `showPostcodeField: false`) — nesting this inside the postcode
  field's block would have hidden the "no addresses" warning on exactly those
  screens, which are the ones that need it most.
-->
<ng-template #lookupFeedback>
  @if (showLookupFeedback && isLoading) {
    <p class="mt-1 text-xs text-slate500">Looking up addresses...</p>
  }
  <!-- A lookup miss is a warning, not a validation error: the address can
       still be typed in by hand, so this stays amber rather than red. -->
  @if (showLookupFeedback && lookupError) {
    <p class="mt-1 flex items-center gap-1 text-[11px] text-amber600" role="status">
      <svg class="h-3.5 w-3.5 shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495ZM10 6a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0v-3.5A.75.75 0 0 1 10 6Zm0 7.5a.9.9 0 1 0 0-1.8.9.9 0 0 0 0 1.8Z"
        />
      </svg>
      <span>{{ lookupError }}</span>
    </p>
  }
</ng-template>

@if (controlOnly) {
  <app-input-reusable
    [label]="label"
    [placeholder]="placeholder"
    type="text"
    [size]="size"
    [control]="externalPostcodeControl"
    [required]="required"
    textCase="uppercase"
    [externalErrorMessage]="externalErrorMessage"
    (focusout)="markAsTouched()"
  ></app-input-reusable>
  <ng-container [ngTemplateOutlet]="lookupFeedback"></ng-container>
} @else {
  <section class="w-full">
    <form [formGroup]="form" class="space-y-4">
      <div [class]="fieldGridClass">
        @if (showPostcodeField) {
          <div>
            <app-input-reusable
              [label]="label"
              [required]="required"
              [placeholder]="placeholder"
              type="text"
              [size]="size"
              [control]="form.controls.postcode"
              textCase="uppercase"
              [externalErrorMessage]="externalErrorMessage"
            ></app-input-reusable>

            <ng-container [ngTemplateOutlet]="lookupFeedback"></ng-container>
          </div>
        }

        @if (showAddressField) {
          <div>
            <!-- The dropdown only earns its place when the lookup actually returned
                 something; with nothing to pick from it becomes a dead end, so the
                 field falls back to the plain input below and the address is typed.
                 The lookup doesn't list every premise, so the dropdown is typeable
                 too: an address missing from the list is entered and saved as-is. -->
            @if (useAddressDropdown && availableAddresses.length > 0) {
              <app-select
                [form]="form"
                [label]="addressLabel"
                controlName="address"
                [size]="size"
                [required]="true"
                [allowCustomValue]="true"
                [options]="availableAddresses"
                [placeholder]="isLoading ? 'Loading address...' : addressSelectPlaceholder"
                [disabled]="isLoading"
              ></app-select>
            } @else {
              <app-input-reusable
                [label]="addressLabel"
                [placeholder]="isLoading ? 'Loading address...' : addressPlaceholder"
                type="text"
                [size]="size"
                [control]="form.controls.address"
                [disabled]="isLoading"
                [required]="true"
              ></app-input-reusable>
            }
          </div>
        }

        @if (showCityField) {
          <app-input-reusable
            [label]="cityLabel"
            [placeholder]="isLoading ? 'Loading city...' : cityPlaceholder"
            type="text"
            [size]="size"
            [control]="form.controls.city"
            [disabled]="isLoading"
            [required]="true"
          ></app-input-reusable>
        }

        @if (showCountryField) {
          @if (countryOptions.length > 0) {
            <app-select
              [form]="form"
              [label]="countryLabel"
              controlName="country"
              [size]="size"
              [options]="countryOptions"
              [placeholder]="isLoading ? 'Loading country...' : countryPlaceholder"
              [disabled]="isLoading"
              [required]="true"
            ></app-select>
          } @else {
            <app-input-reusable
              [label]="countryLabel"
              [placeholder]="isLoading ? 'Loading country...' : countryPlaceholder"
              type="text"
              [size]="size"
              [control]="form.controls.country"
              [required]="true"
              [disabled]="isLoading"
            ></app-input-reusable>
          }
        }
      </div>

      <!-- Address-only instance: the postcode (and its feedback slot) is rendered by
           a sibling instance, so surface the warning here instead of losing it. -->
      @if (!showPostcodeField) {
        <ng-container [ngTemplateOutlet]="lookupFeedback"></ng-container>
      }
    </form>
  </section>
}
